/**
 * The demo's queue-cleared payoff (SKE-4234).
 *
 * When the visitor reviews the LAST pending post — the queue hits 100% — the
 * demo pays it off: a short, restrained burst of confetti on the light client
 * side. The progress-bar flourish (MarketerMirror) and the auto-opening 3-door
 * conversion modal (useDemoConversion, on the same `cleared` state) complete the
 * moment; this component owns only the confetti.
 *
 * The confetti fires only when the action that CLEARED the queue was an approve
 * — emptying the queue by sending the last post back (request-change) is not a
 * "celebrate" beat, so it gets the flourish + CTA without the burst.
 *
 * LIGHT SIDE ONLY
 * ---------------
 * Confetti must never rain over the dark "Your Sked" team pane (brand kit). So
 * the canvas is measured to — and clipped at — the client pane's bounds
 * (`[data-tour="client"]`, the browser-chrome frame). On the mobile flip there
 * is no such element and no dark pane is ever on screen beside the client, so it
 * falls back to the full viewport. The real reviewer path never mounts this.
 *
 * PORTAL / FIXED / ONE-SHOT
 * -------------------------
 * The canvas is a `position: fixed` body portal — a sibling of the approver
 * `App`, never an ancestor, so it can't re-anchor the approver's own fixed
 * elements (demoReveal.ts). It fires exactly once per visit (a ref guard, held
 * whether or not the confetti actually runs) and removes itself when the pieces
 * have fallen. Under `prefers-reduced-motion: reduce` no canvas is created at
 * all — the modal and the mirror's static "Queue cleared" state carry the beat.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Confetti from 'react-confetti'

import { useDemoMirror } from './useDemoMirror'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/** Pieces to throw — restrained, not a full-screen party. */
const PIECES = 160

/** How long the emit takes; kept short so the whole beat reads in ~1.2s. */
const EMIT_MS = 600

/** Hard ceiling on the canvas lifetime, in case `onConfettiComplete` never fires. */
const MAX_LIFETIME_MS = 2400

/**
 * Sked-brand-ish palette, chosen to read on the client pane's white ground:
 * the lavender primary, a lighter tint, the approval green, a warm accent, and
 * an ink for contrast.
 */
const BRAND_COLORS = ['#4f31ff', '#8b78ff', '#22c55e', '#f5b13d', '#1f2937']

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

/**
 * The client pane's viewport rect, or the whole viewport when the pane isn't in
 * the DOM (the mobile flip). Never returns a zero-area rect.
 */
const clientRect = (): Rect => {
  const el = document.querySelector('[data-tour="client"]')
  if (el) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) {
      return { top: r.top, left: r.left, width: r.width, height: r.height }
    }
  }
  return {
    top: 0,
    left: 0,
    width: globalThis.window?.innerWidth ?? 0,
    height: globalThis.window?.innerHeight ?? 0,
  }
}

const DemoPayoff: React.FC = () => {
  const { progress, activity } = useDemoMirror()
  const reduced = usePrefersReducedMotion()

  // Fires once per visit, whether or not confetti actually runs — a reduced-
  // motion visitor who later turns motion back on shouldn't get a late burst.
  const firedRef = useRef(false)
  const [rect, setRect] = useState<Rect | null>(null)

  const cleared =
    progress.total > 0 && progress.pending === 0 && progress.reviewed > 0

  // Confetti celebrates an APPROVAL, not a queue emptied by sending the last
  // post back. `activity` is derived from the store in the same render as
  // `progress` (unlike the effect-driven `latestNotice`), so the most-recent
  // status action here is exactly the one that cleared the queue — no race.
  const clearedByApprove =
    activity.find((notice) => notice.kind === 'status')?.status === 'approved'

  useEffect(() => {
    if (!cleared || firedRef.current) {
      return
    }
    firedRef.current = true

    // Reduced motion, or a queue cleared by a request-change: no burst. The
    // progress flourish + the conversion CTA still mark the "you're done" beat.
    if (reduced || !clearedByApprove) {
      return
    }

    setRect(clientRect())
    const timer = window.setTimeout(() => setRect(null), MAX_LIFETIME_MS)
    return () => window.clearTimeout(timer)
  }, [cleared, reduced, clearedByApprove])

  if (!rect) {
    return null
  }

  return createPortal(
    <Confetti
      width={rect.width}
      height={rect.height}
      numberOfPieces={PIECES}
      tweenDuration={EMIT_MS}
      recycle={false}
      colors={BRAND_COLORS}
      onConfettiComplete={() => setRect(null)}
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        zIndex: 55,
        pointerEvents: 'none',
      }}
    />,
    document.body
  )
}

export default DemoPayoff
